import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Cube, ChartLineUp, UsersThree, FileText, DownloadSimple, ArrowRight, Check,
  Sparkle, Stack, Trophy, ShieldCheck, SquaresFour, Lightning, Plus,
} from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell } from "recharts";

const HERO_BG = "https://images.unsplash.com/photo-1508450859948-4e04fabaa4ea?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const IMG_AI = "https://images.unsplash.com/photo-1503387837-b154d5074bd2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const IMG_TEAM = "https://images.pexels.com/photos/8961008/pexels-photo-8961008.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const HERO_CHART = [
  { phase: "LAND", budget: 120, actual: 116 },
  { phase: "FOUND", budget: 95, actual: 103 },
  { phase: "FRAME", budget: 140, actual: 132 },
  { phase: "MEP", budget: 80, actual: 90 },
  { phase: "FINISH", budget: 110, actual: 105 },
];

const MARQUEE = [
  "LAND → CRIBBING → FOUNDATION → FRAMING → FINISHING",
  "BUDGET VS ACTUAL IN REAL TIME",
  "AI BUDGET EXTRACTION FROM PDFs & SCANS",
  "BEST-PRICE VENDOR COMPARISON",
  "7-DAY FREE TRIAL",
];

const PHASES = [
  ["01", "LAND & PERMITS"],
  ["02", "CRIBBING & FOUNDATION"],
  ["03", "FRAMING & ROUGH-IN"],
  ["04", "FINISHING & HANDOVER"],
];

const FEATURES = [
  { icon: ChartLineUp, title: "Budget vs Actual", desc: "Every line item from land to finishing, with instant variance alerts the moment costs move.", span: "md:col-span-2" },
  { icon: Sparkle, title: "AI Budget Extraction", desc: "Drop in a PDF, photo, or spreadsheet — GPT reads it into structured line items and flags overcharges.", img: IMG_AI, span: "md:col-span-1" },
  { icon: Trophy, title: "Best-Price Vendors", desc: "A vendor directory with spend history and best-price comparisons for smarter quoting.", span: "md:col-span-1" },
  { icon: Stack, title: "Reusable Cost Library", desc: "Save standard trade costs once; drop them into any project with rolling-average price trends.", span: "md:col-span-1" },
  { icon: FileText, title: "Quotation Builder", desc: "Assemble estimates, export polished PDFs, and email them — then convert straight to line items.", span: "md:col-span-1" },
  { icon: ShieldCheck, title: "Team, Roles & Audit", desc: "Invite your team with owner/member roles and a full activity audit trail for accountability.", img: IMG_TEAM, span: "md:col-span-1" },
  { icon: SquaresFour, title: "Portfolio Dashboard", desc: "Charts, summary cards, cost-per-sqft, and a portfolio-wide profit roll-up in one view.", span: "md:col-span-2" },
  { icon: DownloadSimple, title: "Excel / CSV Sync", desc: "Bulk-import budgets from spreadsheets and export a full cost report with one click.", span: "md:col-span-1" },
];

const TIER_PLANS = [
  { id: "starter", name: "Starter", monthly: 29, annual: 299, users: "1 user", tagline: "Solo builders" },
  { id: "business", name: "Business", monthly: 59, annual: 599, users: "Up to 3 users", tagline: "Small teams", popular: true },
  { id: "pro", name: "Pro", monthly: 99, annual: 1049, users: "Up to 10 users", tagline: "Growing firms" },
];

const TESTIMONIALS = [
  { quote: "We caught a $14k concrete overcharge in week one. BuildTrack paid for itself before the foundation was even poured.", name: "Marcus Deller", role: "GC · Deller Custom Homes" },
  { quote: "Budget vs actual across six active builds, in one screen. My bank loves the portfolio PDF at draw time.", name: "Priya Nandakumar", role: "Developer · Northline Builds" },
  { quote: "The AI budget import turned a 3-hour spreadsheet job into 5 minutes. The vendor best-price view is gold.", name: "Tobias Reyes", role: "Estimator · Reyes Commercial" },
];

const STATS = [
  ["$40M+", "Build costs tracked"],
  ["1,200+", "Projects managed"],
  ["15%", "Avg. overspend caught"],
  ["7-day", "Free trial"],
];

const FAQ = [
  { q: "What is BuildTrack?", a: "BuildTrack is construction cost-management software for residential and commercial builders. Track budget vs actual, vendors, quotations and AI budget extraction across every phase, from land cost to finishing." },
  { q: "Who is it for?", a: "General contractors, home builders, developers and commercial builders who need to control costs, compare vendor pricing and see budget vs actual in real time." },
  { q: "Is there a free trial?", a: "Yes — every new account gets a 7-day free trial with full access. No credit card required to start." },
  { q: "How much does it cost?", a: "Three plans: Starter $29/mo, Business $59/mo (up to 3 users), and Pro $99/mo (up to 10 users) — or save with annual billing. Secure checkout via Stripe, cancel anytime." },
  { q: "Can BuildTrack read my existing budgets?", a: "Yes. Upload a PDF, photo or spreadsheet and our AI extracts structured line items, suggests phases and flags overcharges against your history." },
  { q: "Can I invite my team?", a: "Absolutely. Invite team members with owner/member roles, and every change is captured in a full activity audit trail." },
];

export default function Landing() {
  const { user } = useAuth();
  const authed = user && user !== false && user !== null;
  const [annual, setAnnual] = useState(true);
  const [faqOpen, setFaqOpen] = useState(0);
  const dest = authed ? "/dashboard" : "/register";

  return (
    <div className="min-h-screen bg-background text-[#0A0A0A]">
      {/* Nav */}
      <header data-testid="header-nav" className="h-16 border-b-2 border-[#0A0A0A] flex items-center justify-between px-4 sm:px-6 lg:px-10 sticky top-0 bg-white z-50">
        <div className="flex items-center gap-2">
          <img src="/jnia-logo.png" alt="JNIA-BuildTrack" className="h-11 w-auto object-contain" />
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {authed ? (
            <Link to="/dashboard" data-testid="landing-open-app" className="inline-flex items-center gap-1 px-5 py-2.5 bg-[#F59E0B] text-[#0A0A0A] font-bold text-sm uppercase tracking-wider border-2 border-[#0A0A0A] shadow-[3px_3px_0_0_#0A0A0A] hover-lift">
              Open app <ArrowRight size={15} />
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="landing-login" className="hidden sm:inline-flex items-center px-4 py-2.5 font-bold text-sm uppercase tracking-wider hover:bg-[#0A0A0A] hover:text-white transition-colors">
                Sign in
              </Link>
              <Link to="/register" data-testid="header-start-trial-btn" className="inline-flex items-center gap-1 px-4 sm:px-5 py-2.5 bg-[#F59E0B] text-[#0A0A0A] font-bold text-xs sm:text-sm uppercase tracking-wider border-2 border-[#0A0A0A] shadow-[3px_3px_0_0_#0A0A0A] hover-lift">
                Start free trial
              </Link>
            </>
          )}
        </div>
      </header>

      {/* Hero */}
      <section data-testid="hero-section" className="grid lg:grid-cols-2 border-b-2 border-[#0A0A0A]">
        <div className="flex flex-col justify-center px-4 sm:px-6 lg:px-10 py-16 lg:py-24 reveal">
          <div className="inline-flex items-center gap-2 self-start border-2 border-[#0A0A0A] px-3 py-1 mb-6">
            <Lightning size={14} weight="fill" className="text-[#F59E0B]" />
            <span className="label-caps text-[#0A0A0A]">Construction cost management</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95] mb-6">
            Stop bleeding margins.<br /><span className="bg-[#F59E0B] px-2">Track every cent.</span>
          </h1>
          <p className="text-base lg:text-lg text-gray-600 max-w-md mb-8 font-medium">
            The cost-management platform built for residential & commercial builders — budget vs actual,
            vendors, quotations and AI budget extraction, from land cost to finishing.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to={dest} data-testid="hero-start-trial-btn" className="inline-flex items-center gap-2 px-8 py-4 bg-[#F59E0B] text-[#0A0A0A] font-bold uppercase tracking-widest border-2 border-[#0A0A0A] shadow-[5px_5px_0_0_#0A0A0A] hover-lift">
              {authed ? "Open app" : "Start 7-day free trial"} <ArrowRight size={18} />
            </Link>
            <Link to={authed ? "/dashboard" : "/login"} data-testid="hero-sign-in-btn" className="inline-flex items-center px-8 py-4 bg-white text-[#0A0A0A] font-bold uppercase tracking-widest border-2 border-[#0A0A0A] shadow-[5px_5px_0_0_#0A0A0A] hover-lift">
              {authed ? "Dashboard" : "Sign in"}
            </Link>
          </div>
          <p className="text-xs text-gray-500 mt-5 font-medium">No credit card to start · Full access for 7 days</p>
        </div>

        {/* Right: dashboard mockup over grayscale construction bg */}
        <div className="relative bg-[#0A0A0A] min-h-[380px] lg:min-h-0 overflow-hidden border-t-2 lg:border-t-0 lg:border-l-2 border-[#0A0A0A]">
          <img src={HERO_BG} alt="Concrete building under construction" className="absolute inset-0 w-full h-full object-cover grayscale opacity-25" />
          <div className="relative h-full flex items-center justify-center p-6 lg:p-10">
            <div className="w-full max-w-md bg-white border-2 border-[#0A0A0A] shadow-[8px_8px_0_0_#F59E0B] reveal-scale">
              <div className="flex items-center justify-between px-4 py-3 border-b-2 border-[#0A0A0A]">
                <span className="label-caps">Budget vs Actual</span>
                <span className="font-mono-fig text-xs px-2 py-0.5 bg-[#10B981] text-white font-bold">ON TRACK</span>
              </div>
              <div className="px-4 pt-4">
                <div className="flex items-baseline gap-2">
                  <span className="font-mono-fig text-3xl font-bold">$545K</span>
                  <span className="font-mono-fig text-sm text-[#10B981]">−2.1% vs budget</span>
                </div>
              </div>
              <div className="px-2 pb-4 pt-2">
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={HERO_CHART} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barGap={2}>
                    <XAxis dataKey="phase" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#0A0A0A" }} tickLine={false} />
                    <Bar dataKey="budget" fill="#0A0A0A" isAnimationActive />
                    <Bar dataKey="actual" isAnimationActive>
                      {HERO_CHART.map((e, i) => (
                        <Cell key={i} fill={e.actual > e.budget ? "#EF4444" : "#F59E0B"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div data-testid="social-proof" className="border-b-2 border-[#0A0A0A] bg-[#0A0A0A] text-[#F59E0B] py-3 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee">
          {[0, 1].map((k) => (
            <div key={k} className="flex items-center shrink-0">
              {MARQUEE.map((m) => (
                <span key={m} className="font-mono-fig text-xs sm:text-sm font-semibold tracking-wider px-6 flex items-center gap-6">
                  {m} <span className="text-white/40">◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Lifecycle strip */}
      <section data-testid="lifecycle-strip" className="grid grid-cols-2 md:grid-cols-4 border-b-2 border-[#0A0A0A] divide-x-2 divide-[#0A0A0A]">
        {PHASES.map(([n, label]) => (
          <div key={n} className="p-6 lg:p-8 hover:bg-[#F3F4F6] transition-colors">
            <div className="font-mono-fig text-3xl lg:text-4xl font-bold text-[#F59E0B]">{n}</div>
            <div className="font-heading font-bold text-sm lg:text-base mt-2 uppercase tracking-tight">{label}</div>
          </div>
        ))}
      </section>

      {/* Features bento */}
      <section className="px-4 sm:px-6 lg:px-10 py-16 lg:py-28 border-b-2 border-[#0A0A0A]">
        <p className="label-caps mb-3">Everything you need</p>
        <h2 className="font-heading text-3xl lg:text-5xl font-black uppercase tracking-tighter mb-12 max-w-3xl leading-none">
          One ledger for the entire build lifecycle.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 stagger" data-testid="features-grid">
          {FEATURES.map((f) => (
            f.img ? (
              <div key={f.title} data-testid={`feature-card-${f.title.toLowerCase().split(" ")[0]}`}
                className={`relative min-h-[240px] border-2 border-[#0A0A0A] overflow-hidden shadow-[4px_4px_0_0_#0A0A0A] hover-lift ${f.span}`}>
                <img src={f.img} alt={f.title} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/20" />
                <div className="relative h-full flex flex-col justify-end p-6 text-white">
                  <f.icon size={26} weight="bold" className="mb-3 text-[#F59E0B]" />
                  <h3 className="font-heading text-xl font-bold mb-2">{f.title}</h3>
                  <p className="text-sm text-white/80">{f.desc}</p>
                </div>
              </div>
            ) : (
              <div key={f.title} className={`bg-white border-2 border-[#0A0A0A] p-6 lg:p-8 shadow-[4px_4px_0_0_#0A0A0A] hover-lift flex flex-col ${f.span}`}>
                <f.icon size={28} weight="bold" className="mb-4" />
                <h3 className="font-heading text-lg lg:text-xl font-bold mb-2">{f.title}</h3>
                <p className="text-sm text-gray-600">{f.desc}</p>
              </div>
            )
          ))}
        </div>
      </section>

      {/* Social proof */}
      <section data-testid="testimonials-section" className="px-4 sm:px-6 lg:px-10 py-16 lg:py-28 border-b-2 border-[#0A0A0A]">
        <p className="label-caps mb-3">Trusted by builders</p>
        <h2 className="font-heading text-3xl lg:text-5xl font-black uppercase tracking-tighter mb-12 max-w-2xl leading-none">
          Crews that stopped guessing.
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <figure key={i} data-testid={`testimonial-${i}`} className="border-2 border-[#0A0A0A] bg-white p-6 flex flex-col shadow-[4px_4px_0_0_#0A0A0A]">
              <div className="text-[#F59E0B] text-lg mb-4 tracking-widest">★★★★★</div>
              <blockquote className="text-base leading-relaxed flex-1">“{t.quote}”</blockquote>
              <figcaption className="mt-6 pt-4 border-t-2 border-[#0A0A0A]">
                <div className="font-heading font-bold">{t.name}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wider">{t.role}</div>
              </figcaption>
            </figure>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#0A0A0A] border-2 border-[#0A0A0A] mt-12">
          {STATS.map(([num, label]) => (
            <div key={label} className="bg-white px-6 py-8 text-center">
              <div className="font-heading text-3xl lg:text-4xl font-black tracking-tighter">{num}</div>
              <div className="text-xs text-gray-500 uppercase tracking-wider mt-1">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section data-testid="pricing-section" id="pricing" className="px-4 sm:px-6 lg:px-10 py-16 lg:py-28 bg-[#F3F4F6] border-b-2 border-[#0A0A0A]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="label-caps mb-3">Plans for every crew</p>
          <h2 className="font-heading text-3xl lg:text-5xl font-black uppercase tracking-tighter mb-8 leading-none">
            Pricing that scales with your team.
          </h2>
          <div className="relative inline-block mb-12">
            <span data-testid="annual-ribbon" className="absolute -top-3 right-0 z-10 bg-[#10B981] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border-2 border-[#0A0A0A]">
              Up to 2 months free
            </span>
            <div data-testid="pricing-billing-toggle" className="inline-flex border-2 border-[#0A0A0A] bg-white shadow-[3px_3px_0_0_#0A0A0A]">
              <button onClick={() => setAnnual(false)} data-testid="pricing-monthly" className={`px-6 py-2 font-bold text-sm uppercase tracking-wider transition-colors ${!annual ? "bg-[#0A0A0A] text-white" : "text-[#0A0A0A]"}`}>Monthly</button>
              <button onClick={() => setAnnual(true)} data-testid="pricing-annual" className={`px-6 py-2 font-bold text-sm uppercase tracking-wider transition-colors ${annual ? "bg-[#0A0A0A] text-white" : "text-[#0A0A0A]"}`}>Annual <span className="text-[#10B981]">save ~15%</span></button>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6 items-stretch">
          {TIER_PLANS.map((t) => (
            <div key={t.id} data-testid={`landing-tier-${t.id}`}
              className={`relative bg-white p-8 flex flex-col border-2 ${t.popular ? "border-[#F59E0B] shadow-[8px_8px_0_0_#F59E0B]" : "border-[#0A0A0A] shadow-[5px_5px_0_0_#0A0A0A]"}`}>
              {t.popular && <div className="absolute -top-3 left-8 bg-[#F59E0B] text-black text-xs font-bold uppercase tracking-wider px-2 py-0.5 border-2 border-[#0A0A0A]">Most popular</div>}
              <div className="font-heading font-black text-2xl uppercase tracking-tight">{t.name}</div>
              <div className="text-sm text-gray-500">{t.tagline}</div>
              <div className="mt-5 flex items-end gap-1">
                <span className="font-mono-fig text-5xl font-bold" data-testid={`landing-price-${t.id}`}>${annual ? t.annual : t.monthly}</span>
                <span className="text-gray-500 mb-2">/{annual ? "yr" : "mo"}</span>
              </div>
              <div className="mt-2 inline-flex self-start label-caps border border-[#0A0A0A] px-2 py-0.5">{t.users}</div>
              {annual && (t.monthly * 12 - t.annual) > 0 && (
                <p className="mt-2 text-xs font-bold text-[#059669]" data-testid={`landing-savings-${t.id}`}>
                  Save ${t.monthly * 12 - t.annual}/yr vs monthly
                </p>
              )}
              <Link to={dest} data-testid={`landing-cta-${t.id}`}
                className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 font-bold uppercase tracking-widest border-2 border-[#0A0A0A] hover-lift ${t.popular ? "bg-[#F59E0B] text-[#0A0A0A] shadow-[4px_4px_0_0_#0A0A0A]" : "bg-white text-[#0A0A0A]"}`}>
                {authed ? "Open app" : "Start free trial"}
              </Link>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-gray-500 mt-8">7-day free trial · No card to start · Secure checkout via Stripe · Cancel anytime</p>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="px-4 sm:px-6 lg:px-10 py-16 lg:py-28 border-b-2 border-[#0A0A0A]">
        <p className="label-caps mb-3">FAQ</p>
        <h2 className="font-heading text-3xl lg:text-5xl font-black uppercase tracking-tighter mb-12 max-w-2xl leading-none">
          Questions, answered.
        </h2>
        <div className="max-w-3xl border-t-2 border-[#0A0A0A]">
          {FAQ.map((f, i) => (
            <div key={i} className="border-b-2 border-[#0A0A0A]">
              <button
                onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}
                data-testid={`faq-item-${i}`}
                className="w-full flex items-center justify-between gap-4 py-5 text-left group"
              >
                <span className="font-heading font-bold text-base lg:text-lg">{f.q}</span>
                <Plus size={20} weight="bold" className={`shrink-0 transition-transform duration-300 ${faqOpen === i ? "rotate-45" : ""}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${faqOpen === i ? "max-h-56" : "max-h-0"}`}>
                <p className="text-sm text-gray-600 pb-5 pr-8 leading-relaxed">{f.a}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="final-cta" className="px-4 sm:px-6 lg:px-10 py-20 lg:py-28 bg-[#0A0A0A] text-white text-center border-b-2 border-[#F59E0B]">
        <h2 className="font-heading text-3xl lg:text-6xl font-black uppercase tracking-tighter mb-4 leading-none">
          Ready to build smarter?
        </h2>
        <p className="text-gray-400 max-w-xl mx-auto mb-8 font-medium">Start tracking your true build costs today. Free for 7 days — no credit card required.</p>
        <Link to={dest} data-testid="final-cta-btn" className="inline-flex items-center gap-2 px-10 py-4 bg-[#F59E0B] text-[#0A0A0A] font-bold uppercase tracking-widest border-2 border-[#F59E0B] hover-lift">
          {authed ? "Open app" : "Start your free trial"} <ArrowRight size={18} />
        </Link>
      </section>

      {/* Footer */}
      <footer data-testid="footer-links" className="px-4 sm:px-6 lg:px-10 py-12 grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
        <div className="flex items-center gap-2">
          <div className="bg-white rounded px-2 py-1 inline-flex">
            <img src="/jnia-logo.png" alt="JNIA-BuildTrack" className="h-10 w-auto object-contain" />
          </div>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 md:justify-center">
          <Link to="/register" className="text-sm font-medium text-gray-600 hover:text-[#0A0A0A] link-underline">Get started</Link>
          <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-[#0A0A0A] link-underline">Sign in</Link>
          <a href="#pricing" className="text-sm font-medium text-gray-600 hover:text-[#0A0A0A] link-underline">Pricing</a>
        </div>
        <div className="font-mono-fig text-xs text-gray-500 md:text-right">© {new Date().getFullYear()} BuildTrack — Construction Cost Management</div>
      </footer>
    </div>
  );
}
